/*
  Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { ref, shallowRef, readonly, onUnmounted } from "vue";
import rocketChatAuthService from "@/services/RocketChatAuthService";

/**
 * Default configuration for WebSocket connection
 */
const DEFAULT_CONFIG = {
  reconnectInterval: 1000,
  maxReconnectInterval: 30000,
  reconnectDecay: 1.5,
  maxReconnectAttempts: 10,
  heartbeatInterval: 25000,
  messageBufferSize: 100,
};

/**
 * Connection states
 */
export const ConnectionState = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  AUTHENTICATING: "authenticating",
  READY: "ready",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

/**
 * Vue composable for managing WebSocket connections to Rocket.Chat.
 * Implements the DDP (Distributed Data Protocol) used by Rocket.Chat.
 * Provides automatic reconnection, authentication, and room subscriptions.
 *
 * @param {Object} config - Configuration options
 * @returns {Object} Composable API
 */
export function useChatWebSocket(config = {}) {
  const options = { ...DEFAULT_CONFIG, ...config };

  // Reactive state
  const connectionState = ref(ConnectionState.DISCONNECTED);
  const lastError = ref(null);
  const reconnectAttempts = ref(0);
  const messageBuffer = shallowRef([]);
  const subscribedRooms = ref(new Set());

  // Internal state (non-reactive)
  let socket = null;
  let heartbeatTimer = null;
  let reconnectTimer = null;
  let isClosedByUser = false;
  let messageId = 0;
  let pendingMethods = new Map();

  // Event handlers
  const handlers = {
    onConnected: [],
    onDisconnected: [],
    onReady: [],
    onMessage: [],
    onRoomMessage: [],
    onTyping: [],
    onPresence: [],
    onError: [],
    onReconnecting: [],
  };

  /**
   * Registers an event handler
   */
  const on = (event, handler) => {
    if (handlers[event]) {
      handlers[event].push(handler);
    }
    return () => off(event, handler);
  };

  /**
   * Removes an event handler
   */
  const off = (event, handler) => {
    if (handlers[event]) {
      const index = handlers[event].indexOf(handler);
      if (index > -1) {
        handlers[event].splice(index, 1);
      }
    }
  };

  /**
   * Emits an event to all registered handlers
   */
  const emit = (event, ...args) => {
    if (handlers[event]) {
      handlers[event].forEach((handler) => handler(...args));
    }
  };

  /**
   * Generates a unique message ID
   */
  const generateId = () => {
    return String(++messageId);
  };

  /**
   * Connects to the Rocket.Chat WebSocket server
   *
   * @returns {Promise<void>}
   */
  const connect = () => {
    return new Promise((resolve, reject) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      const wsUrl = rocketChatAuthService.getWebSocketUrl();
      if (!wsUrl) {
        const error = new Error("WebSocket URL not configured");
        lastError.value = error;
        connectionState.value = ConnectionState.ERROR;
        reject(error);
        return;
      }

      isClosedByUser = false;
      connectionState.value = ConnectionState.CONNECTING;

      try {
        socket = new WebSocket(wsUrl);

        socket.onopen = () => {
          connectionState.value = ConnectionState.CONNECTED;
          reconnectAttempts.value = 0;
          lastError.value = null;

          // Send DDP connect message
          sendRaw({
            msg: "connect",
            version: "1",
            support: ["1", "pre2", "pre1"],
          });

          emit("onConnected");
        };

        socket.onclose = (event) => {
          stopHeartbeat();
          connectionState.value = ConnectionState.DISCONNECTED;

          if (!isClosedByUser && !event.wasClean) {
            scheduleReconnect();
          }

          emit("onDisconnected", event);
        };

        socket.onerror = (error) => {
          lastError.value = error;
          connectionState.value = ConnectionState.ERROR;
          emit("onError", error);
          reject(error);
        };

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            handleMessage(message, resolve, reject);
          } catch (e) {
            console.error("[ChatWebSocket] Failed to parse message:", e);
          }
        };
      } catch (error) {
        lastError.value = error;
        connectionState.value = ConnectionState.ERROR;
        emit("onError", error);
        reject(error);
      }
    });
  };

  /**
   * Disconnects from the WebSocket server
   */
  const disconnect = () => {
    isClosedByUser = true;
    stopHeartbeat();
    clearReconnectTimer();

    if (socket) {
      socket.close(1000, "User disconnected");
      socket = null;
    }

    connectionState.value = ConnectionState.DISCONNECTED;
    messageBuffer.value = [];
    subscribedRooms.value = new Set();
    pendingMethods.clear();
  };

  /**
   * Sends a raw message to the server
   *
   * @param {Object} message - Message to send
   * @returns {boolean} True if sent, false otherwise
   */
  const sendRaw = (message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
      return true;
    }
    return false;
  };

  /**
   * Sends a message with buffering support
   *
   * @param {Object} message - Message to send
   * @returns {boolean} True if sent immediately, false if buffered
   */
  const send = (message) => {
    if (sendRaw(message)) {
      return true;
    }

    // Buffer message for later
    if (messageBuffer.value.length < options.messageBufferSize) {
      messageBuffer.value = [...messageBuffer.value, message];
    } else {
      console.warn("[ChatWebSocket] Message buffer full, dropping message");
    }
    return false;
  };

  /**
   * Calls a Rocket.Chat method via DDP
   *
   * @param {string} method - Method name
   * @param {Array} params - Method parameters
   * @returns {Promise<any>}
   */
  const callMethod = (method, params = []) => {
    return new Promise((resolve, reject) => {
      const id = generateId();

      pendingMethods.set(id, { resolve, reject });

      const message = {
        msg: "method",
        method,
        id,
        params,
      };

      if (!sendRaw(message)) {
        pendingMethods.delete(id);
        reject(new Error("WebSocket not connected"));
      }

      // Timeout for method call
      setTimeout(() => {
        if (pendingMethods.has(id)) {
          pendingMethods.delete(id);
          reject(new Error(`Method ${method} timed out`));
        }
      }, 30000);
    });
  };

  /**
   * Handles incoming DDP messages
   */
  const handleMessage = (message, connectResolve, connectReject) => {
    switch (message.msg) {
      case "connected":
        startHeartbeat();
        authenticate(connectResolve, connectReject);
        break;

      case "ping":
        sendRaw({ msg: "pong" });
        break;

      case "pong":
        // Heartbeat response received
        break;

      case "result":
        handleMethodResult(message);
        break;

      case "ready":
        // Subscription ready
        break;

      case "added":
      case "changed":
      case "removed":
        handleCollectionChange(message);
        break;

      case "error":
        handleError(message, connectReject);
        break;

      case "failed":
        console.error("[ChatWebSocket] DDP version negotiation failed");
        connectionState.value = ConnectionState.ERROR;
        if (connectReject) {
          connectReject(new Error("DDP version negotiation failed"));
        }
        break;

      default:
        emit("onMessage", message);
    }
  };

  /**
   * Authenticates with Rocket.Chat using stored credentials
   */
  const authenticate = async (connectResolve, connectReject) => {
    connectionState.value = ConnectionState.AUTHENTICATING;

    try {
      const creds = await rocketChatAuthService.getCredentials();
      if (!creds) {
        throw new Error("No credentials available");
      }

      const result = await callMethod("login", [
        {
          resume: creds.authToken,
        },
      ]);

      if (result && result.id) {
        connectionState.value = ConnectionState.READY;
        flushMessageBuffer();
        resubscribeRooms();
        emit("onReady");
        if (connectResolve) {
          connectResolve();
        }
      } else {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      console.error("[ChatWebSocket] Authentication failed:", error);
      lastError.value = error;
      connectionState.value = ConnectionState.ERROR;
      emit("onError", error);
      if (connectReject) {
        connectReject(error);
      }
    }
  };

  /**
   * Handles method result messages
   */
  const handleMethodResult = (message) => {
    const pending = pendingMethods.get(message.id);
    if (pending) {
      pendingMethods.delete(message.id);

      if (message.error) {
        pending.reject(new Error(message.error.message || "Method error"));
      } else {
        pending.resolve(message.result);
      }
    }
  };

  /**
   * Handles collection change messages (added, changed, removed)
   */
  const handleCollectionChange = (message) => {
    const { collection, fields } = message;

    if (collection === "stream-room-messages") {
      // New message in a room
      if (fields && fields.args) {
        const [roomMessage] = fields.args;
        emit("onRoomMessage", roomMessage);
      }
    } else if (collection === "stream-notify-room") {
      // Room notifications (typing, etc.)
      if (fields && fields.args) {
        const [eventName, ...args] = fields.args;
        if (eventName === "typing") {
          emit("onTyping", ...args);
        }
      }
    } else if (collection === "stream-notify-user") {
      // User notifications
      if (fields && fields.args) {
        emit("onMessage", { type: "user-notification", data: fields.args });
      }
    }
  };

  /**
   * Handles error messages
   */
  const handleError = (message, connectReject) => {
    const error = new Error(message.reason || "Unknown error");
    lastError.value = error;
    emit("onError", error);

    if (connectReject) {
      connectReject(error);
    }
  };

  /**
   * Subscribes to a room's messages
   *
   * @param {string} roomId - The room ID to subscribe to
   * @returns {Promise<void>}
   */
  const subscribeRoom = async (roomId) => {
    if (subscribedRooms.value.has(roomId)) {
      return;
    }

    const subId = generateId();

    // Subscribe to room messages
    sendRaw({
      msg: "sub",
      id: subId,
      name: "stream-room-messages",
      params: [roomId, false],
    });

    // Subscribe to room notifications (typing, etc.)
    const notifySubId = generateId();
    sendRaw({
      msg: "sub",
      id: notifySubId,
      name: "stream-notify-room",
      params: [`${roomId}/typing`, false],
    });

    subscribedRooms.value = new Set([...subscribedRooms.value, roomId]);
  };

  /**
   * Unsubscribes from a room
   *
   * @param {string} roomId - The room ID to unsubscribe from
   */
  const unsubscribeRoom = (roomId) => {
    subscribedRooms.value = new Set(
      [...subscribedRooms.value].filter((id) => id !== roomId),
    );
    // Note: DDP unsubscribe would require tracking subscription IDs
  };

  /**
   * Resubscribes to all previously subscribed rooms after reconnection
   */
  const resubscribeRooms = () => {
    const rooms = [...subscribedRooms.value];
    subscribedRooms.value = new Set();

    rooms.forEach((roomId) => {
      subscribeRoom(roomId);
    });
  };

  /**
   * Sends a chat message to a room
   *
   * @param {string} roomId - The room ID
   * @param {string} text - The message text
   * @returns {Promise<Object>}
   */
  const sendChatMessage = async (roomId, text) => {
    return callMethod("sendMessage", [
      {
        _id: generateId(),
        rid: roomId,
        msg: text,
      },
    ]);
  };

  /**
   * Sends typing indicator
   *
   * @param {string} roomId - The room ID
   * @param {string} username - The username
   * @param {boolean} isTyping - Whether the user is typing
   */
  const sendTyping = (roomId, username, isTyping) => {
    sendRaw({
      msg: "method",
      method: "stream-notify-room",
      id: generateId(),
      params: [`${roomId}/typing`, username, isTyping],
    });
  };

  /**
   * Starts the heartbeat timer
   */
  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        sendRaw({ msg: "ping" });
      }
    }, options.heartbeatInterval);
  };

  /**
   * Stops the heartbeat timer
   */
  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  /**
   * Schedules a reconnection attempt
   */
  const scheduleReconnect = () => {
    if (reconnectAttempts.value >= options.maxReconnectAttempts) {
      console.error("[ChatWebSocket] Max reconnection attempts reached");
      connectionState.value = ConnectionState.ERROR;
      return;
    }

    connectionState.value = ConnectionState.RECONNECTING;
    reconnectAttempts.value++;

    const delay = Math.min(
      options.reconnectInterval *
        Math.pow(options.reconnectDecay, reconnectAttempts.value - 1),
      options.maxReconnectInterval,
    );

    emit("onReconnecting", reconnectAttempts.value, delay);

    reconnectTimer = setTimeout(() => {
      if (!isClosedByUser) {
        connect().catch(() => {
          // Reconnect will be scheduled again by onclose handler
        });
      }
    }, delay);
  };

  /**
   * Clears the reconnect timer
   */
  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  /**
   * Flushes the message buffer
   */
  const flushMessageBuffer = () => {
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      messageBuffer.value.length > 0
    ) {
      const messages = [...messageBuffer.value];
      messageBuffer.value = [];

      messages.forEach((message) => {
        sendRaw(message);
      });
    }
  };

  /**
   * Returns whether the connection is ready for use
   */
  const isReady = () => {
    return (
      connectionState.value === ConnectionState.READY &&
      socket &&
      socket.readyState === WebSocket.OPEN
    );
  };

  /**
   * Returns whether the connection is active (connected or authenticating)
   */
  const isConnected = () => {
    return socket && socket.readyState === WebSocket.OPEN;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    // State (readonly)
    connectionState: readonly(connectionState),
    lastError: readonly(lastError),
    reconnectAttempts: readonly(reconnectAttempts),
    bufferedMessages: readonly(messageBuffer),
    subscribedRooms: readonly(subscribedRooms),

    // Methods
    connect,
    disconnect,
    isConnected,
    isReady,

    // Room operations
    subscribeRoom,
    unsubscribeRoom,

    // Chat operations
    sendChatMessage,
    sendTyping,

    // Low-level
    callMethod,
    send,

    // Event handlers
    on,
    off,
  };
}

export default useChatWebSocket;
